import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  gql,
  useQuery,
} from '@apollo/client';
import {
  ElementRegistryProvider,
  FormFileTransportProvider,
  PageRenderConfigProvider,
} from '@eaco/forms-ui-web';
import { APP_GRAPHQL_ENDPOINT, APP_MAPBOX_ACCESS_TOKEN } from '../../config';
import { pageElementRegistry } from '@/lib/pageElementRegistry';
import { makePortalFileTransport } from '@/lib/portalFileTransport';
import PageReadOnly from './PageReadOnly';
import PagePrint from './PagePrint';

/**
 * Internal preview render target: `/p/preview/:oid/:token`. The render service
 * (headless Chromium) loads this with a short-lived owner JWT, so the page
 * renders through the normal authed resolvers — exactly like the editor — and
 * the service screenshots it. The OID is carried for storage correlation; the
 * page uid is read from the token (`previewUid`).
 */

const PAGE_QUERY = gql`
  query previewPageDocument($uid: String!) {
    pageDocument(uid: $uid) {
      id
      uid
      name
      content
    }
  }
`;

// Flag the render service polls before screenshotting.
declare global {
  interface Window {
    __PAGE_PREVIEW_READY__?: boolean;
    __PAGE_PREVIEW_ERROR__?: string;
  }
}

function decodeToken(token: string): {
  previewUid?: string;
  clientDId?: number | string;
} {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      previewUid: payload.previewUid,
      clientDId: payload.clientUsers?.[0]?.clientDId,
    };
  } catch {
    return {};
  }
}

// The first page's flat block list (mirrors the editor's page model):
// a `pages` container → first page's blocks; legacy `page` elements → the
// first; otherwise the flat top-level list.
function firstPageElements(content: any): any[] {
  const els: any[] = content?.elements ?? [];
  const container = els.find((el) => el?.typeId === 'pages');
  if (container) return container.elements?.[0]?.elements ?? [];
  const pageEls = els.filter((el) => el?.typeId === 'page');
  if (pageEls.length > 0) return pageEls[0].elements ?? [];
  return els;
}

// Every page's block list, in order (for the full-document PDF).
function allPages(content: any): { elements: any[] }[] {
  const els: any[] = content?.elements ?? [];
  const container = els.find((el) => el?.typeId === 'pages');
  if (container)
    return (container.elements ?? []).map((pg: any) => ({
      elements: pg?.elements ?? [],
    }));
  const pageEls = els.filter((el) => el?.typeId === 'page');
  if (pageEls.length > 0)
    return pageEls.map((pg: any) => ({ elements: pg?.elements ?? [] }));
  return [{ elements: els }];
}

const PreviewContent: React.FC<{ uid: string; pdf?: boolean }> = ({
  uid,
  pdf,
}) => {
  const { data, error } = useQuery(PAGE_QUERY, { variables: { uid } });

  const page = data?.pageDocument;
  const content = useMemo(() => {
    const raw = page?.content;
    if (!raw) return null;
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  }, [page?.content]);

  useEffect(() => {
    if (error) {
      window.__PAGE_PREVIEW_ERROR__ = String(error.message);
      window.__PAGE_PREVIEW_READY__ = true; // let the service capture the error state
    }
  }, [error]);

  const pages = useMemo(() => (content ? allPages(content) : []), [content]);
  const firstPage = useMemo(() => (content ? firstPageElements(content) : []), [content]);

  // The view calls this once its files have resolved (usePageFiles). The images
  // they point at are in the DOM by then; let them and the fonts finish, capped
  // so one slow image can't hold the capture forever.
  const markReady = useCallback(() => {
    const pending = Array.from(document.images).filter((img) => !img.complete);
    const settled = Promise.all([
      document.fonts?.ready,
      ...pending.map((img) => img.decode().catch(() => undefined)),
    ]);
    const cap = new Promise((resolve) => setTimeout(resolve, 5000));
    Promise.race([settled, cap]).then(() => {
      window.__PAGE_PREVIEW_READY__ = true;
    });
  }, []);

  if (error) {
    return (
      <div style={{ padding: 24, fontFamily: 'sans-serif', color: '#b00' }}>
        Preview error: {String(error.message)}
      </div>
    );
  }
  if (!content) return <div style={{ minHeight: '100vh' }} />;

  if (pdf) {
    return (
      <PagePrint
        name={page?.name ?? ''}
        meta={content?.meta ?? {}}
        pages={pages}
        onReady={markReady}
      />
    );
  }

  return (
    <PageReadOnly
      name={page?.name ?? ''}
      meta={content?.meta ?? {}}
      elements={firstPage}
      onReady={markReady}
    />
  );
};

const PagePreviewView: React.FC = () => {
  const { token } = useParams<{ oid: string; token: string }>();
  const [search] = useSearchParams();
  const pdfMode = search.get('mode') === 'pdf';
  // The render service passes an in-network gateway URL (the baked
  // VITE endpoint host isn't resolvable from inside the render container).
  const gqlEndpoint = search.get('gql') || APP_GRAPHQL_ENDPOINT;
  const { previewUid, clientDId } = useMemo(
    () => (token ? decodeToken(token) : {}),
    [token],
  );

  const client = useMemo(() => {
    return new ApolloClient({
      link: new HttpLink({
        uri: gqlEndpoint,
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          'x-client-id': clientDId != null ? String(clientDId) : '',
          'x-access-type': 'next-interface',
        },
      }),
      cache: new InMemoryCache(),
      defaultOptions: { query: { fetchPolicy: 'network-only' } },
    });
  }, [token, clientDId, gqlEndpoint]);

  if (!token || !previewUid) {
    return <div style={{ padding: 24 }}>Invalid preview request.</div>;
  }

  const transport = useMemo(() => makePortalFileTransport(client), [client]);

  return (
    <ApolloProvider client={client}>
      <FormFileTransportProvider value={transport}>
        <PageRenderConfigProvider
          value={{ mapboxToken: APP_MAPBOX_ACCESS_TOKEN }}
        >
          <ElementRegistryProvider registry={pageElementRegistry}>
            <PreviewContent uid={previewUid} pdf={pdfMode} />
          </ElementRegistryProvider>
        </PageRenderConfigProvider>
      </FormFileTransportProvider>
    </ApolloProvider>
  );
};

export default PagePreviewView;
